/* ------------------------------------------------------------------
   News / media page behaviour.

   Liquid cannot reliably sort metaobject entries — the sort filter
   returns them in handle order — and a band can mix metaobject
   entries, blog articles and manual blocks, so the ordering, the
   upcoming/past split and the "load more" all happen here.
------------------------------------------------------------------ */
(function () {
  'use strict';

  var TODAY = new Date().toISOString().slice(0, 10);

  function tiles(grid) {
    return Array.prototype.slice.call(grid.querySelectorAll(':scope > li'));
  }

  function sortGrid(grid, ascending) {
    tiles(grid)
      .sort(function (a, b) {
        var da = a.firstElementChild.dataset.date || '';
        var db = b.firstElementChild.dataset.date || '';
        if (da === db) return 0;
        return (da < db ? -1 : 1) * (ascending ? 1 : -1);
      })
      .forEach(function (li) { grid.appendChild(li); });
  }

  /* Move future-dated tiles out of the history grid and into "Coming up".
     Doing it here rather than in Liquid means an event needs no attention
     when its date passes. */
  function splitUpcoming(band) {
    var up = band.querySelector('[data-mgrid="upcoming"]');
    var past = band.querySelector('[data-mgrid="past"]');
    if (!up || !past) return;

    tiles(past).forEach(function (li) {
      if ((li.firstElementChild.dataset.date || '') > TODAY) up.appendChild(li);
    });

    sortGrid(up, true);     // soonest first
    sortGrid(past, false);  // newest first

    [['upcoming', up], ['past', past]].forEach(function (pair) {
      var section = band.querySelector('[data-mpart="' + pair[0] + '"]');
      if (section) section.hidden = tiles(pair[1]).length === 0;
    });
  }

  function columnCount(grid) {
    var cols = getComputedStyle(grid).gridTemplateColumns;
    return cols ? cols.split(' ').filter(Boolean).length : 4;
  }

  /* Limits are counted in column units — a wide tile is worth two — and
     snapped to whole rows at the current breakpoint, so a band never ends on
     a ragged half-row. If what's left over wouldn't fill a row there's no
     point hiding it behind a button, so it just shows. */
  function applyLimit(band) {
    var limit = parseInt(band.dataset.limitUnits, 10);
    if (!limit) return;
    var step = parseInt(band.dataset.stepUnits, 10) || limit;

    var grids = Array.prototype.slice.call(
      band.querySelectorAll('[data-mgrid="past"], [data-mgrid="single"]')
    );
    if (!grids.length) return;

    var cols = columnCount(grids[0]);
    var all = [];
    grids.forEach(function (grid) { all = all.concat(tiles(grid)); });

    var total = all.reduce(function (sum, li) {
      return sum + (parseInt(li.firstElementChild.dataset.units, 10) || 1);
    }, 0);

    var wanted = band._shownUnits || limit;
    // round the limit to whole rows for this breakpoint
    var rows = Math.max(1, Math.round(wanted / cols));
    var allowed = rows * cols;
    // a remainder smaller than one row isn't worth a button
    if (total - allowed < cols) allowed = total;

    var used = 0;
    var overflow = false;
    all.forEach(function (li) {
      var units = parseInt(li.firstElementChild.dataset.units, 10) || 1;
      if (used + units <= allowed) {
        used += units;
        li.hidden = false;
      } else {
        li.hidden = true;
        overflow = true;
      }
    });

    var more = band.querySelector('[data-mmore]');
    if (more) {
      more.parentElement.hidden = !overflow;
      if (!more._bound) {
        more._bound = true;
        more.addEventListener('click', function () {
          band._shownUnits = (band._shownUnits || limit) + step;
          applyLimit(band);
        });
      }
    }
  }

  /* The Upcoming pill is added here rather than in Liquid: Liquid has no
     reliable date comparison for the string dates the tiles carry, and this
     keeps "upcoming" true on the day rather than at publish time. */
  function markUpcoming(root) {
    root.querySelectorAll('.f26-mtile[data-date]').forEach(function (tile) {
      if ((tile.dataset.date || '') <= TODAY) return;
      if (tile.querySelector('.f26-mtile__badge--up')) return;
      var badges = tile.querySelector('.f26-mtile__badges');
      if (!badges) return;
      var pill = document.createElement('span');
      pill.className = 'f26-mtile__badge f26-mtile__badge--up';
      pill.textContent = 'Upcoming';
      badges.insertBefore(pill, badges.firstChild);
      tile.dataset.upcoming = 'true';
    });
  }

  function initBand(band) {
    markUpcoming(band);
    band.querySelectorAll('[data-mgrid="single"]').forEach(function (g) { sortGrid(g, false); });
    splitUpcoming(band);
    applyLimit(band);
  }

  function initThumbs(root) {
    root.querySelectorAll('img[data-f26-fallback]').forEach(function (img) {
      function swap() {
        var fb = img.getAttribute('data-f26-fallback');
        if (!fb || img.src === fb) return;
        img.removeAttribute('data-f26-fallback');
        img.src = fb;
      }
      img.addEventListener('error', function () {
        if (img.hasAttribute('data-f26-fallback')) { swap(); return; }
        var tile = img.closest('.f26-mtile');
        if (!tile) return;
        img.remove();
        tile.classList.add('f26-mtile--ink', 'f26-mtile--flat');
      });
      img.addEventListener('load', function () {
        if (img.naturalWidth <= 120) swap();
      });
      if (img.complete && img.naturalWidth && img.naturalWidth <= 120) swap();
    });
  }

  function initJumpNav(nav) {
    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    if (!links.length || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        links.forEach(function (link) {
          link.classList.toggle('is-active', link.getAttribute('href') === '#' + entry.target.id);
        });
      });
    }, { rootMargin: '-45% 0px -50% 0px' });

    links.forEach(function (link) {
      var target = document.getElementById(link.getAttribute('href').slice(1));
      if (target) observer.observe(target);
    });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-f26-band]').forEach(initBand);
    initThumbs(root || document);
    (root || document).querySelectorAll('[data-f26-jump]').forEach(initJumpNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* The row snapping depends on the column count, so redo it on resize. */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('[data-f26-band]').forEach(applyLimit);
    }, 150);
  });

  /* Theme editor: re-run for the section that just changed. */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
